import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Card,
  Grid,
  Stack,
  Chip,
} from "@mui/material";
import {
  Clear as ClearIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import { fDateDdMmmYyyy } from "../../utils/time";

const TeacherDetailsDialog = ({ open, onClose, teacher, isMobile }) => {
  if (!teacher) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Teacher Details - {teacher.full_name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Card elevation={2} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <SchoolIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">Personal Information</Typography>
              </Box>
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Staff Number:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.staff_number}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Full Name:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.full_name}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Gender:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.gender}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Date of Birth:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {teacher.date_of_birth ? fDateDdMmmYyyy(teacher.date_of_birth) : "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>National ID:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.national_id || "N/A"}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Phone:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.phone_number || "N/A"}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Email:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.email || "N/A"}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Address:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {teacher.address || "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <SchoolIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="secondary">Employment Information</Typography>
              </Box>
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Employment Status:</Typography>
                  <Chip
                    label={teacher.employment_status}
                    color={teacher.employment_status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Employment Date:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {teacher.employment_date ? fDateDdMmmYyyy(teacher.employment_date) : "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Qualification:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.qualification || "N/A"}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Specialization:</Typography>
                  <Typography variant="body2" fontWeight="medium">{teacher.specialization || "N/A"}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Active:</Typography>
                  <Chip
                    label={teacher.is_active ? "Active" : "Inactive"}
                    color={teacher.is_active ? "success" : "error"}
                    size="small"
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
          
          {(teacher.class_teacher_allocations?.length > 0 || teacher.subject_teacher_allocations?.length > 0) && (
            <Box mt={3}>
              <Typography variant="h6" color="info.main" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <SchoolIcon sx={{ mr: 1 }} />
                Teaching Assignments
              </Typography>
              
              {teacher.class_teacher_allocations?.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Class Teacher:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {teacher.class_teacher_allocations.map((allocation, index) => (
                      <Chip
                        key={index}
                        label={`${allocation.class?.name || 'N/A'} - ${allocation.stream?.name || 'N/A'}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {teacher.subject_teacher_allocations?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Subject Teacher:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {teacher.subject_teacher_allocations.map((allocation, index) => (
                      <Chip
                        key={index}
                        label={allocation.class_stream_subject_paper?.subject_paper?.subject?.subject_name || 'N/A'}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} fullWidth={isMobile}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeacherDetailsDialog;
